// Phase P.12 — Calendar helpers shared by Day/Week/Month view components.
// Kept small + pure so they can be unit-tested without React.

export const HOUR_MIN = 7;    // hard lower bound per spec
export const HOUR_MAX = 21;   // hard upper bound (exclusive-ish — end line at 21:00)

/**
 * Given a list of bookings, decide which hour window to render so all jobs
 * fit AND we never exceed the 07:00–21:00 hard bounds.
 *
 * - Default window is 08:00 → 20:00.
 * - If any booking in `bookings` starts between 07:00 (incl) and 08:00
 *   (excl) we widen the low bound to 07:00.
 * - If any booking starts between 20:00 (incl) and 21:00 (excl) OR runs
 *   past 20:00 we widen the high bound to 21:00.
 * - Bookings whose start is outside 07:00–21:00 are effectively clamped
 *   to those bounds by the visual renderer (positioned at the edge).
 */
export function computeHourWindow(bookings) {
  let lo = 8;
  let hi = 20;
  for (const b of bookings || []) {
    const start = b?.scheduled_start ? new Date(b.scheduled_start) : null;
    if (!start || Number.isNaN(start.getTime())) continue;
    const h = start.getHours() + start.getMinutes() / 60;
    if (h < 8 && h >= HOUR_MIN) lo = HOUR_MIN;
    if (h >= 20 && h < HOUR_MAX) hi = HOUR_MAX;
  }
  return { lo, hi };
}

/**
 * Map a booking to a `{top, height}` in pixels for a vertical time-grid.
 * `hourPx` is the visual density (px per hour). Falls back to 60 minutes
 * if the booking has no scheduled_end.
 */
export function bookingBox(b, { lo, hourPx }) {
  const start = new Date(b.scheduled_start);
  const endIso = b.scheduled_end && b.scheduled_end.length > 0
    ? b.scheduled_end
    : new Date(start.getTime() + 60 * 60 * 1000).toISOString();
  const end = new Date(endIso);
  const startH = start.getHours() + start.getMinutes() / 60;
  const endH = end.getHours() + end.getMinutes() / 60;
  const top = Math.max(0, (startH - lo) * hourPx);
  const height = Math.max(24, (endH - startH) * hourPx);
  return { top, height };
}

/**
 * Payment-status → tailwind class fragments used to tint booking cards on
 * all three views. Kept in one place so future colour tweaks stay
 * consistent.
 */
export function paymentTintClasses(b) {
  const status = (b?.payment_status || "").toLowerCase();
  const jobStatus = (b?.status || "").toLowerCase();
  if (status === "paid") {
    return {
      border: "border-l-emerald-500 border-emerald-200",
      bg: "bg-emerald-50",
      dot: "bg-emerald-500",
      label: "Paid",
    };
  }
  if (status === "overdue") {
    return {
      border: "border-l-rose-500 border-rose-200",
      bg: "bg-rose-50",
      dot: "bg-rose-500",
      label: "Overdue",
    };
  }
  if (status === "awaiting_payment" || status === "pending_manager_approval") {
    return {
      border: "border-l-amber-500 border-amber-200",
      bg: "bg-amber-50",
      dot: "bg-amber-500",
      label: status === "pending_manager_approval" ? "Awaiting approval" : "Awaiting payment",
    };
  }
  if (jobStatus === "completed") {
    // Completed but not (yet) marked paid — use a distinct purple tint.
    return {
      border: "border-l-purple-500 border-purple-200",
      bg: "bg-purple-50",
      dot: "bg-purple-500",
      label: "Completed",
    };
  }
  // Draft / unissued / scheduled — neutral blue tint so the card still
  // reads as "on the books" without any warning colours.
  return {
    border: "border-l-sky-500 border-sky-200",
    bg: "bg-sky-50",
    dot: "bg-sky-500",
    label: "Scheduled",
  };
}

/** "HH:MM" — 24h clock, 2-digit hour + minute. */
export function fmtHm(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

/** "HH:MM–HH:MM" range. Falls back to just start if no end. */
export function fmtHmRange(b) {
  const s = fmtHm(b?.scheduled_start);
  const e = b?.scheduled_end ? fmtHm(b.scheduled_end) : "";
  return e ? `${s}–${e}` : s;
}

/**
 * Phase P.14 — assign overlap lanes to a list of bookings so that
 * concurrent bookings (same time slot, different staff) render side-by-side
 * without hiding each other.
 *
 * Returns an array of `{ b, lane, laneCount }` where:
 *   - `lane` is the 0-indexed column this booking occupies inside its
 *     overlap cluster.
 *   - `laneCount` is the width of its cluster (max concurrent bookings in
 *     the overlap chain). All items in the same cluster share the same
 *     laneCount so their column widths align.
 *
 * Algorithm:
 *   1. Sort bookings by scheduled_start.
 *   2. Sweep to build clusters — a cluster ends when we hit a booking
 *      whose start >= max(end) of the current cluster.
 *   3. Inside each cluster, greedy-assign each booking to the lowest lane
 *      that's free (its end <= new item's start).
 *   4. Stamp `laneCount` on every item = the max lane used in the cluster + 1.
 *
 * `hourPx` is not needed here — pixel positioning is done by `bookingBox`.
 */
export function assignOverlapLanes(bookings) {
  const items = (bookings || []).map((b) => {
    const start = b?.scheduled_start ? new Date(b.scheduled_start).getTime() : NaN;
    const durMin = Number(b?.scheduled_duration_minutes) || 60;
    const endMs = b?.scheduled_end
      ? new Date(b.scheduled_end).getTime()
      : (Number.isFinite(start) ? start + durMin * 60_000 : NaN);
    return { b, start, end: endMs };
  }).filter((x) => Number.isFinite(x.start) && Number.isFinite(x.end));
  items.sort((a, b) => a.start - b.start || a.end - b.end);

  const out = new Array(items.length);
  let clusterStart = 0;
  let clusterEnd = -Infinity;
  let laneEnds = []; // laneEnds[i] = end time of most recent booking in lane i

  const flushCluster = (endIdx) => {
    const maxLane = Math.max(0, ...out.slice(clusterStart, endIdx).map((x) => x.lane));
    for (let i = clusterStart; i < endIdx; i++) {
      out[i].laneCount = maxLane + 1;
    }
  };

  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (it.start >= clusterEnd) {
      // Start a new cluster — flush the previous one's laneCount first.
      if (i > clusterStart) flushCluster(i);
      clusterStart = i;
      clusterEnd = it.end;
      laneEnds = [it.end];
      out[i] = { b: it.b, lane: 0, laneCount: 1 };
      continue;
    }
    // Extend cluster: pick the lowest lane whose latest end <= it.start.
    let lane = -1;
    for (let j = 0; j < laneEnds.length; j++) {
      if (laneEnds[j] <= it.start) { lane = j; break; }
    }
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); }
    else laneEnds[lane] = it.end;
    clusterEnd = Math.max(clusterEnd, it.end);
    out[i] = { b: it.b, lane, laneCount: 1 };
  }
  if (items.length > 0) flushCluster(items.length);
  return out;
}

/**
 * Phase P.14 — compute inset side-by-side positioning inside a time slot.
 *
 * Returns `{ leftPct, widthPct, overflow }`:
 *   - `leftPct` / `widthPct`: 0..100 percentages inside the parent column.
 *   - `overflow`: true when this booking is in an overflow lane (lane >= MAX_LANES-1
 *     of a cluster whose size exceeds MAX_LANES). Callers can render a
 *     "+N more" chip instead of the full card.
 *
 * We use inline percentages (not tailwind fractional widths) so 5-lane
 * clusters degrade gracefully instead of being clamped to a fixed set.
 */
export const MAX_VISIBLE_LANES = 4;
export function laneRect(lane, laneCount) {
  const visibleCount = Math.min(laneCount, MAX_VISIBLE_LANES);
  const cappedLane = Math.min(lane, visibleCount - 1);
  const width = 100 / visibleCount;
  return {
    leftPct: cappedLane * width,
    widthPct: width,
    overflow: laneCount > MAX_VISIBLE_LANES && lane >= MAX_VISIBLE_LANES - 1,
  };
}

/** Compare same-day. Accepts Date or ISO string. */
export function isSameYmd(a, b) {
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
}
